import { useState, useEffect } from "react";
import { getUserProfile, updateUserProfile, updateUserDetails, GetUserDetails, getCountries, getStates, getTimezones, getLanguages, normalizeTimezone } from "../service/rpc";
import { getSession } from "../service/auth";
import toast from "react-hot-toast";
import "../styles/Profile.css";
import { Dropdown } from 'primereact/dropdown';


export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const defaultFormData = {
    name: "",
    email: "",
    street: "",
    street2: "",
    city: "",
    zip: "",
    country_id: "",
    state_id: "",
    lang: "en_US",
    tz: normalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [initialData, setInitialData] = useState(defaultFormData);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [languages, setLanguages] = useState([]);
  const languageOptions = languages.map(([value, label]) => ({ value, label }));
  const timezoneOptions = timezones.map(([value, label]) => ({ value, label }));

  useEffect(() => {
    fetchProfile();
    loadCountries();
    loadTimezones();
    loadLanguages();
  }, []);


  const isChanged = JSON.stringify(formData) !== JSON.stringify(initialData);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const session = getSession();
      if (!session || !session.uid) return;

      // Fetch both partner info and user details
      const [partnerData, userData] = await Promise.all([
        getUserProfile(session.uid),
        GetUserDetails(session.uid)
      ]);

      if (partnerData) {
        setProfile(partnerData);
        const user = userData && userData.length > 0 ? userData[0] : {};

        const data = {
          name: partnerData.name || "",
          email: partnerData.email || "",
          street: partnerData.street || "",
          street2: partnerData.street2 || "",
          city: partnerData.city || "",
          zip: partnerData.zip || "",
          country_id: partnerData.country_id ? partnerData.country_id[0] : "",
          state_id: partnerData.state_id ? partnerData.state_id[0] : "",
          lang: user.lang || "en_US",
          tz: normalizeTimezone(user.tz || Intl.DateTimeFormat().resolvedOptions().timeZone),
          profile_type: partnerData.profile_type || user.profile_type || ""
        };

        setFormData(data);
        setInitialData(data);

        if (partnerData.country_id) loadStates(partnerData.country_id[0]);
      }
    } catch (err) {
      console.error("Failed to fetch profile", err);
      setError(err.message || "Failed to load profile details.");
    } finally {
      setLoading(false);
    }
  };
  const loadCountries = async () => {
    try { setCountries(await getCountries()); }
    catch (err) { console.error("Countries load failed", err); }
  };

  const loadStates = async (countryId) => {
    try { setStates(await getStates(countryId)); }
    catch (err) { console.error("States load failed", err); }
  };

  const loadTimezones = async () => {
    try { setTimezones(await getTimezones()); }
    catch (err) { console.error("Timezones load failed", err); }
  };

  const loadLanguages = async () => {
    try { setLanguages(await getLanguages()); }
    catch (err) { console.error("Languages load failed", err); }
  };

  const handleCountryChange = (e) => {
    const cid = e.value ?? "";
    setFormData(prev => ({ ...prev, country_id: cid, state_id: "" }));
    if (!cid) {
      setStates([]);
      return;
    }
    loadStates(cid);
  };

  const handleStateChange = (e) => {
    const sid = e.value ?? "";
    const sel = states.find(s => s.id === parseInt(sid));
    if (sel?.country_id) {
      setFormData(prev => ({ ...prev, state_id: sid, country_id: sel.country_id[0] }));
    } else {
      setFormData(prev => ({ ...prev, state_id: sid }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const session = getSession();
      await Promise.all([
        updateUserProfile(profile.id, {
          name: formData.name,
          email: formData.email,
          street: formData.street,
          street2: formData.street2,
          city: formData.city,
          zip: formData.zip,
          country_id: formData.country_id ? parseInt(formData.country_id) : false,
          state_id: formData.state_id ? parseInt(formData.state_id) : false
        }),
        updateUserDetails(session.uid, {
          lang: formData.lang,
          tz: normalizeTimezone(formData.tz)
        })
      ]);
      toast.success("Profile updated successfully!");
      fetchProfile();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="pf-wrap">
        <div className="pf-error" style={{ border: '1px solid #fee2e2', background: '#fff', padding: '40px', borderRadius: '12px' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '18px' }}>Error Loading Profile</p>
          <p style={{ fontSize: '14px', marginTop: '8px', color: '#64748b', maxWidth: '400px', margin: '8px auto' }}>{error}</p>
          <button
            className="pf-btn"
            style={{ marginTop: '24px', background: '#6b7fc4', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
            onClick={fetchProfile}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!profile && !loading) {
    return <div className="pf-error">Unable to load profile. Please sign in again.</div>;
  }

  return (
    <div className="pf-wrap">
      <div className="page-header">
        <h1 className="page-header__title">Profile</h1>
      </div>
      {/* Unified form card */}
      <div className={`pf-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading profile...</p>
          </div>
        )}
        {/* Action row — now at top right, only when changed */}
        {isChanged && (
          <div style={{
            position: "absolute", top: "0.80rem", right: "1.25rem",
            display: "flex", alignItems: "center", gap: "8px"
          }}>
            <button
              onClick={() => fetchProfile()}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                height: "32px", padding: "0 14px",
                background: "#F3F4F6", border: "0.5px solid #E5E7EB",
                borderRadius: "999px", fontSize: "13px", fontWeight: 500,
                color: "#6B7280", cursor: "pointer"
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#E5E7EB"}
              onMouseLeave={e => e.currentTarget.style.background = "#F3F4F6"}
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                height: "32px", padding: "0 16px",
                background: saving ? "#818CF8" : "#4F46E5", border: "none",
                borderRadius: "999px", fontSize: "13px", fontWeight: 500,
                color: "#fff", cursor: saving ? "not-allowed" : "pointer"
              }}
              onMouseEnter={e => { if (!saving) e.currentTarget.style.background = "#4338CA"; }}
              onMouseLeave={e => { if (!saving) e.currentTarget.style.background = "#4F46E5"; }}
            >
              {saving ? "Saving…" : " Save"}
            </button>

          </div>
        )}

        <div className="pf-grid">
          {/* Row 1: Name + Email */}
          <div className="pf-field">
            <label>Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
            />
          </div>
          <div className="pf-field">
            <label>Email Address</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@example.com"
            />
          </div>

          {/* Row 2: Street */}
          <div className="pf-field">
            <label>Street</label>
            <input
              type="text"
              value={formData.street}
              onChange={e => setFormData({ ...formData, street: e.target.value })}
              placeholder="Street address"
            />
          </div>

          {/* Row 3: Street 2 */}
          <div className="pf-field">
            <label>Street 2</label>
            <input
              type="text"
              value={formData.street2}
              onChange={e => setFormData({ ...formData, street2: e.target.value })}
              placeholder="Apt, suite, etc."
            />
          </div>

          {/* Row 4: City + Zip */}
          <div className="pf-field">
            <label>City</label>
            <input
              type="text"
              value={formData.city}
              onChange={e => setFormData({ ...formData, city: e.target.value })}
              placeholder="City"
            />
          </div>
          <div className="pf-field">
            <label>Zip Code</label>
            <input
              type="text"
              value={formData.zip}
              onChange={e => setFormData({ ...formData, zip: e.target.value })}
              placeholder="Zip"
            />
          </div>

          <div className="pf-field">
            <label>Country</label>
            <Dropdown
              value={formData.country_id}
              options={countries}
              onChange={handleCountryChange}
              optionLabel="name"
              optionValue="id"
              placeholder="Select Country"
              filter
              className="pf-dropdown"
              panelClassName="pf-dropdown-panel"
              filterPlaceholder="Search country"
            />
          </div>

          <div className="pf-field">
            <label>State</label>
            <Dropdown
              value={formData.state_id}
              options={states}
              onChange={handleStateChange}
              optionLabel="name"
              optionValue="id"
              placeholder="Select State"
              filter
              disabled={!formData.country_id}
              emptyMessage={formData.country_id ? "No states found" : "Choose a country first"}
              className="pf-dropdown"
              panelClassName="pf-dropdown-panel"
              filterPlaceholder="Search state"
            />
          </div>

          {/* Row 6: Language + Timezone */}
          <div className="pf-field">
            <label>Language</label>
            <Dropdown
              value={formData.lang}
              options={languageOptions}
              onChange={e => setFormData({ ...formData, lang: e.value ?? "" })}
              optionLabel="label"
              optionValue="value"
              placeholder="Select Language"
              filter
              className="pf-dropdown"
              panelClassName="pf-dropdown-panel"
              filterPlaceholder="Search language"
            />
          </div>
          <div className="pf-field">
            <label>Timezone</label>
            <Dropdown
              value={formData.tz}
              options={timezoneOptions}
              onChange={e => setFormData({ ...formData, tz: e.value ?? "" })}
              optionLabel="label"
              optionValue="value"
              placeholder="Select Timezone"
              filter
              className="pf-dropdown"
              panelClassName="pf-dropdown-panel"
              filterPlaceholder="Search timezone"
            />
          </div>
        </div>

      </div>
    </div>
  );
}
